import React, { useState, useRef } from "react"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Button from "../../../components/ui/Button"

const API_BASE_URL = "https://nesolearn-backend.onrender.com"
const MAX_FILE_SIZE_MB = 500

const VideoLessonUploader = ({ lesson, onChange, updateField }) => {
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState("")
  const fileInputRef = useRef(null)

  const handleFileSelect = async (e) => {
    const file = e?.target?.files?.[0]
    if (!file) return

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`File is too large. Max ${MAX_FILE_SIZE_MB}MB.`)
      return
    }
    if (!file.type.startsWith("video/")) {
      setError("Please choose a video file.")
      return
    }

    setError("")
    setUploading(true)
    setProgress(0)

    try {
      const res = await fetch(`${API_BASE_URL}/api/stream/direct-upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      })
      const { uploadURL, uid, customerCode, error: reqError } = await res.json()
      if (!res.ok || !uploadURL) {
        throw new Error(reqError || "Could not start upload")
      }

      const formData = new FormData()
      formData.append("file", file)

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.upload.addEventListener("progress", (evt) => {
          if (evt.lengthComputable) setProgress(Math.round((evt.loaded / evt.total) * 100))
        })
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve()
          else reject(new Error("Upload failed"))
        })
        xhr.addEventListener("error", () => reject(new Error("Upload failed")))
        xhr.open("POST", uploadURL)
        xhr.send(formData)
      })

      onChange({
        ...lesson,
        cloudflareUid: uid,
        cloudflareCustomerCode: customerCode,
        videoUrl: "", // clear any legacy YouTube URL — Cloudflare is now the source of truth
      })
    } catch (err) {
      console.error("Video upload failed:", err)
      setError("Upload failed. Please try again.")
    } finally {
      setUploading(false)
    }
  }

  const handleReplace = () => {
    onChange({ ...lesson, cloudflareUid: "", cloudflareCustomerCode: "" })
    fileInputRef.current?.value && (fileInputRef.current.value = "")
  }

  const hasVideo = !!lesson?.cloudflareUid

  return (
    <div className="space-y-2">
      {hasVideo ? (
        <div className="space-y-2">
          <div className="aspect-video rounded-md overflow-hidden border border-border bg-black">
            <iframe
              src={`https://customer-${lesson.cloudflareCustomerCode}.cloudflarestream.com/${lesson.cloudflareUid}/iframe`}
              title={lesson?.title || "Video preview"}
              className="w-full h-full"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
              allowFullScreen
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Video uploaded. It may take a few minutes to finish processing before it plays smoothly for learners.
          </p>
          <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" onClick={handleReplace}>
            Replace Video
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground block">Video File</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            disabled={uploading}
            className="text-sm text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-muted file:text-foreground file:text-sm hover:file:bg-muted/80 disabled:opacity-60"
          />
          {uploading && (
            <div className="space-y-1">
              <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-muted-foreground">Uploading… {progress}%</p>
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <p className="text-xs text-muted-foreground">Max {MAX_FILE_SIZE_MB}MB.</p>
        </div>
      )}

      <Input
        label="Duration (minutes)"
        type="number"
        value={lesson?.duration}
        onChange={updateField("duration")}
      />
    </div>
  )
}

export default VideoLessonUploader